/**
 * Player avatar (#42, FR-2.10) — a small reusable presentational component rendered
 * everywhere a player's identity appears (lobby roster, in-game seats, leaderboard,
 * game-over standings). When the player has a profile picture it shows the image;
 * otherwise it shows a neutral initial-letter placeholder, so every surface renders
 * consistently whether or not a picture is set.
 *
 * Deliberately minimal so the future guest-disambiguation work (#36) can absorb it
 * into a single `PlayerLabel` (name + guest badge + short id + avatar) without a
 * rewrite — this owns only the avatar bit.
 */

export interface AvatarProps {
  /** The player's display name — drives the placeholder initial + alt text. */
  name: string;
  /** Same-origin profile-picture URL, if the player has one. */
  avatarUrl?: string;
  /** Rendered edge length in px (square). Default 28. */
  size?: number;
}

export function Avatar({ name, avatarUrl, size = 28 }: AvatarProps): React.JSX.Element {
  const initial = (name.trim()[0] ?? '?').toUpperCase();
  if (avatarUrl) {
    return (
      <img
        className="avatar avatar--img"
        src={avatarUrl}
        // Decorative: the name is always shown adjacent, so an empty alt avoids a
        // redundant screen-reader announcement.
        alt=""
        width={size}
        height={size}
        loading="lazy"
        data-testid="avatar-img"
      />
    );
  }
  return (
    <span
      className="avatar avatar--fallback"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.5) }}
      aria-hidden="true"
      data-testid="avatar-fallback"
    >
      {initial}
    </span>
  );
}
